import { Loader } from '@10xLabs/react-web/components/Loader'
import { NetworkStatus, useQuery } from '@apollo/client'
import { isEmpty } from 'lodash'
import { ReactElement, useEffect } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from 'components/v2/Button'
import { Content, ContentBody, ContentHeader } from 'components/v2/Content'
import EmptyState from 'components/v2/EmptyState'
import Heading from 'components/v2/Heading'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import VehiclesV2List from 'features/Vehicles/components/VehiclesList/List'
import VehiclesV2ListPagination from 'features/Vehicles/components/VehiclesList/Pagination'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { useLogin } from 'features/Login/hooks'
import { canRead, canWrite } from 'features/Users/utils'
import { GET_VEHICLES_SUGGESTIONS, GET_VEHICLES_V2 } from 'features/Vehicles/queries-legacy'
import { mapFiltersToSearchQueryString, mapSearchQueryToInput, mapSearchToFiltersValues } from 'features/Vehicles/utils'
import { IVehiclesFilters } from 'features/Vehicles/types'
import {
  GTDriverSuggestionsResponse,
  GTPartnerSuggestionsResponse,
  GTTVehicle,
  GTTVehiclesResponse,
  GTVehicleSuggestionsResponse
} from 'gql/types.generated'
import { getQueries } from 'utils/route'
import { VehiclesFilters } from 'features/Vehicles/components/VehiclesList/Filters'
import { GET_DRIVERS_SUGGESTIONS } from 'features/Drivers/queries-legacy'
import { GET_PARTNERS_SUGGESTIONS } from 'features/Partners/queries-legacy'

const VEHICLES_LIMIT = 30

const messages = defineMessages({
  helmet: {
    id: 'vehiclesV2.helmet',
    defaultMessage: 'Vehicles'
  }
})

const VehiclesV2View = (): ReactElement => {
  let content: ReactElement
  const intl = useIntl()
  const login = useLogin()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const searchQueries = getQueries(location.search)

  const { data: vehiclesData } = useQuery<{ vehicleSuggestions: GTVehicleSuggestionsResponse }>(
    GET_VEHICLES_SUGGESTIONS,
    {
      variables: {
        input: {
          id: searchQueries.vehicleId
        }
      },
      skip: !searchQueries.vehicleId
    }
  )
  const { data: driversData } = useQuery<{ driverSuggestions: GTDriverSuggestionsResponse }>(GET_DRIVERS_SUGGESTIONS, {
    variables: {
      input: {
        id: searchQueries.driverId
      }
    },
    skip: !searchQueries.driverId
  })
  const { data: partnersData } = useQuery<{ partnerSuggestions: GTPartnerSuggestionsResponse }>(
    GET_PARTNERS_SUGGESTIONS,
    {
      skip: !searchQueries.partnerId,
      variables: {
        input: {
          id: searchQueries.partnerId
        }
      }
    }
  )

  const pageNb = parseInt(searchParams.get('page') ?? '1')
  const filters = mapSearchToFiltersValues(searchQueries, {
    vehicles: vehiclesData?.vehicleSuggestions.items || [],
    drivers: driversData?.driverSuggestions.items || [],
    partners: partnersData?.partnerSuggestions.items || []
  })

  const hasPermissions = canRead(FEATURES.VEHICLE, login.accessToken.policies)
  const filtersApplied = mapSearchQueryToInput(searchQueries)
  const { loading, error, data, refetch, networkStatus } = useQuery<{ tvehicles: GTTVehiclesResponse }>(
    GET_VEHICLES_V2,
    {
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      skip: !hasPermissions,
      variables: {
        pagination: {
          page: pageNb,
          size: VEHICLES_LIMIT
        },
        input: filtersApplied
      }
    }
  )

  useEffect((): void => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  if (!hasPermissions) {
    return <ForbiddenView feature={FEATURES.VEHICLE} />
  }

  if (networkStatus === NetworkStatus.refetch || loading) {
    content = (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (error) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="vehiclesV2.error.description"
            defaultMessage="There was an error while loading your data"
          />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button
            onClick={(): void => {
              refetch()
            }}
            kind="primary"
          >
            <FormattedMessage id="vehiclesV2.error.reload" defaultMessage="Reload" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (isEmpty(data?.tvehicles.items) && pageNb === 1 && isEmpty(filtersApplied)) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage id="vehiclesV2.empty.description" defaultMessage="There was no data found" />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button as={NavLink} to="/vehicles/new" kind="primary">
            <FormattedMessage id="vehiclesV2.empty.action" defaultMessage="+ Add vehicle" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (isEmpty(data?.tvehicles.items)) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="vehiclesV2.empty.filters.description"
            defaultMessage="There was no vehicles found according to your filters"
          />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button as={NavLink} to="/vehicles" kind="primary">
            <FormattedMessage id="vehiclesV2.empty.filters.action" defaultMessage="Clear filters" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else {
    content = (
      <>
        <VehiclesV2List vehicles={data?.tvehicles.items as GTTVehicle[]} />
        <VehiclesV2ListPagination
          page={pageNb}
          totalItems={data?.tvehicles.total ?? 0}
          currentItemsCount={data?.tvehicles.items.length || 0}
          limit={VEHICLES_LIMIT}
          filters={filters}
        />
      </>
    )
  }

  const handleFiltersSubmit = (values: IVehiclesFilters): void => {
    navigate(`/vehicles?${mapFiltersToSearchQueryString(values)}`)
  }

  return (
    <Content>
      <ContentHeader>
        <Heading.H3>
          <FormattedMessage id="vehiclesV2.header.title" defaultMessage="Vehicle list" />
        </Heading.H3>
        {canWrite(FEATURES.VEHICLE, login.accessToken.policies) && (
          <Button as={NavLink} to="/vehicles/new" kind="primary">
            <FormattedMessage id="vehiclesV2.header.add" defaultMessage="+ Add Vehicle" />
          </Button>
        )}
      </ContentHeader>
      <ContentBody>
        <VehiclesFilters filters={filters} onSubmit={handleFiltersSubmit} />
        <section>{content}</section>
      </ContentBody>
    </Content>
  )
}

export default withLastUrl(VehiclesV2View)
